// ==============================|| DESIGN TOKENS - SINGLE SOURCE OF TRUTH ||============================== //
//
// Every colour, radius, shadow and spacing value used by the theme and by the
// shared styled-components in `views/ConstantColors.js` resolves from here.
// Do not hardcode hex values, radii or shadows in components - add a token.

// ---------------------------------------------------------------- brand ramp

export const brand = {
    // Navy - the single canonical primary. Previously duplicated as
    // #213363 / #2E4057 / #1e3a8a across the codebase.
    navy: {
        50: '#EEF2FA',
        100: '#E6EAF2',
        200: '#C3CDE4',
        300: '#8FA0C8',
        400: '#4C63A0',
        500: '#213363',
        600: '#1B2A52',
        700: '#16223F',
        800: '#131F47',
        900: '#0D142B'
    },
    // Teal - secondary
    teal: {
        50: '#E6EDED',
        100: '#D2DFE0',
        200: '#A8C0C3',
        300: '#7FA1A6',
        400: '#699499',
        500: '#4F7C82',
        600: '#43696E',
        700: '#3B5D62',
        800: '#2C4649',
        900: '#1E3033'
    },
    // Forest green - success / positive
    forest: {
        50: '#E6EFED',
        100: '#CDE0DB',
        200: '#9BC0B7',
        300: '#5F9A8B',
        400: '#1F7361',
        500: '#17594A',
        600: '#134B3F',
        700: '#0F3F35',
        800: '#0B2E27',
        900: '#071E19'
    },
    // Amber - warning / pending
    amber: {
        50: '#FEF6EC',
        100: '#FCE9D2',
        200: '#F7C08C',
        300: '#F4A763',
        400: '#F5983F',
        500: '#F28F3B',
        600: '#E07E2B',
        700: '#D47427',
        800: '#A9591C',
        900: '#7A3F13'
    },
    // Red - error / destructive
    red: {
        50: '#FEF2F2',
        100: '#FEE2E2',
        200: '#FECACA',
        300: '#FCA5A5',
        400: '#EF4444',
        500: '#DC2626',
        600: '#C81E1E',
        700: '#B91C1C',
        800: '#911616',
        900: '#5F0F0F'
    },
    // Neutral slate ramp
    grey: {
        50: '#F8FAFC',
        100: '#F1F5F9',
        200: '#E2E8F0',
        300: '#CBD5E1',
        400: '#94A3B8',
        500: '#64748B',
        600: '#475569',
        700: '#334155',
        800: '#1E293B',
        900: '#0F172A'
    },
    white: '#FFFFFF'
};

// ------------------------------------------------------- semantic role scale
// Shape mirrors what `themeColors` in ConstantColors.js already exposed
// (main / light / lighter / dark / contrastText) so call sites stay untouched.

const role = (ramp, { main, light, lighter, dark }) => ({
    main: ramp[main],
    light: ramp[light],
    lighter: ramp[lighter],
    dark: ramp[dark],
    contrastText: brand.white
});

export const semantic = {
    primary: role(brand.navy, { main: 500, light: 400, lighter: 100, dark: 800 }),
    secondary: role(brand.teal, { main: 500, light: 400, lighter: 50, dark: 700 }),
    success: role(brand.forest, { main: 500, light: 400, lighter: 50, dark: 700 }),
    warning: role(brand.amber, { main: 500, light: 300, lighter: 200, dark: 700 }),
    error: role(brand.red, { main: 500, light: 400, lighter: 100, dark: 700 }),
    grey: brand.grey,
    background: {
        default: brand.white,
        paper: brand.grey[50],
        dark: brand.grey[800],
        light: brand.grey[50]
    },
    text: {
        primary: brand.grey[900],
        secondary: brand.grey[600],
        light: brand.grey[400],
        contrastText: brand.white
    }
};

// ------------------------------------------------------------- radius scale
// Replaces the 19 ad-hoc borderRadius values previously in circulation.
// `md` is the app default and matches config.borderRadius.

export const radius = {
    xs: 4,
    sm: 6,
    md: 8,
    lg: 12,
    xl: 16,
    pill: 999
};

// ------------------------------------------------------------ elevation ramp
// Replaces ~40 one-off boxShadow strings. Four steps, one consistent ramp.

export const shadow = {
    none: 'none',
    xs: '0 1px 2px rgba(15, 23, 42, 0.06)',
    sm: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
    md: '0 4px 12px rgba(15, 23, 42, 0.08), 0 2px 4px rgba(15, 23, 42, 0.04)',
    lg: '0 12px 24px rgba(15, 23, 42, 0.10), 0 4px 8px rgba(15, 23, 42, 0.04)',
    // Focus ring, used instead of the browser default outline.
    focus: `0 0 0 3px ${brand.navy[500]}33`
};

// ------------------------------------------------------------------- density
// This app is data-heavy: 38 table screens and long request/acquittal forms.
// Controls are sized for scanning density, not for a marketing page.

export const density = {
    controlHeight: 40,
    controlHeightSmall: 32,
    controlHeightLarge: 48,
    inputPaddingY: 9,
    inputPaddingX: 14,
    buttonPaddingY: 8,
    buttonPaddingX: 18,
    tableCellPaddingY: 10,
    tableCellPaddingX: 16,
    tableCellPaddingDenseY: 6,
    menuItemPaddingY: 8,
    menuItemPaddingX: 16
};

// ------------------------------------------------------------- touch density
// The `density` scale above is tuned for a mouse. On touch devices those
// targets fall under the 44px WCAG 2.5.5 / iOS HIG minimum, so every control
// is scaled back up behind a `(pointer: coarse)` query. Desktop stays dense,
// phones stay tappable - we do not have to pick one.

export const touch = {
    minTarget: 44,
    controlHeight: 44,
    inputPaddingY: 12,
    buttonPaddingY: 11,
    tableCellPaddingY: 14,
    menuItemPaddingY: 12,
    togglePadding: 10,
    // iOS Safari force-zooms the viewport when a focused input renders below
    // 16px. Anything smaller here causes a jarring zoom on every field tap.
    inputFontSize: '1rem'
};

// Media query strings. Kept as raw strings so they work inside plain style
// objects that have no access to `theme.breakpoints`.
export const query = {
    coarse: '@media (pointer: coarse)',
    phone: '@media (max-width: 600px)',
    tablet: '@media (max-width: 900px)'
};

// -------------------------------------------------------------- type ramp
// The ONLY type scale in the app. `typography.js` builds MUI variants from it;
// nothing should re-declare font sizes in a component styleOverride.

// Headings interpolate with the viewport so a page title that fits a desktop
// column does not wrap to three lines on a 360px phone. Body sizes stay fixed:
// fluid body copy hurts readability and 14px already reads well on mobile.

export const type = {
    h1: 'clamp(1.5rem, 1.15rem + 1.5vw, 2rem)',
    h2: 'clamp(1.25rem, 1.05rem + 1.0vw, 1.625rem)',
    h3: 'clamp(1.125rem, 1rem + 0.6vw, 1.375rem)',
    h4: 'clamp(1.0625rem, 1rem + 0.3vw, 1.125rem)',
    h5: '1rem',
    h6: '0.875rem',
    body1: '0.875rem',
    body2: '0.8125rem',
    caption: '0.75rem',
    overline: '0.6875rem'
};

export const weight = {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700
};

// -------------------------------------------------------------------- motion

export const motion = {
    quick: '0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    smooth: '0.24s cubic-bezier(0.4, 0, 0.2, 1)',
    spring: '0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
};

export default { brand, semantic, radius, shadow, density, type, weight, motion };
